import { v4 as uuid } from 'uuid';
const emptyView = (projection) => ({ projection, loading: false, error: null, updatingId: '' });
const sameIntent = (left, right) => left?.targetId === right.targetId
  && left?.body?.enabled === right.body.enabled
  && left?.body?.expected_revision === right.body.expected_revision;

const canToggle = (disposed, write, item, targetType) => (
  !disposed && Boolean(write) && item?.target_type === targetType && item?.manageable === true
);

// Single async lifecycle for the two independent management projections.
export function createSettingsRequestOwner({
  instanceId, onChange, read, write, emptyProjection, normalizeProjection, targetType,
}) {
  let view = emptyView(emptyProjection);
  let generation = 0;
  let disposed = false;
  let reading = null;
  let updating = null;
  let failedRequest = null;
  const publish = (patch) => {
    if (disposed) return;
    view = { ...view, ...patch };
    onChange(view);
  };
  const accept = (value, token) => {
    if (disposed || token !== generation) return view.projection;
    const next = normalizeProjection(value);
    if (next.scope?.instance_id && next.scope.instance_id !== instanceId) {
      throw new Error(`${targetType}_projection_scope_mismatch`);
    }
    const accepted = next.revision >= view.projection.revision ? next : view.projection;
    failedRequest = null;
    publish({ projection: accepted, loading: false, error: null, updatingId: '' });
    return accepted;
  };
  const perform = (kind, invoke, request = null) => {
    const token = ++generation;
    const controller = new AbortController();
    const task = { controller, request, promise: null };
    publish({ loading: kind === 'read', updatingId: request?.targetId ?? '', error: null });
    task.promise = Promise.resolve().then(() => (
      disposed || token !== generation ? view.projection : invoke(controller.signal)
    ))
      .then((value) => accept(value, token))
      .catch((error) => {
        if (!disposed && token === generation && error?.name !== 'AbortError') {
          failedRequest = request;
          publish({ error, loading: false, updatingId: '' });
        }
        throw error;
      })
      .finally(() => {
        if (reading === task) reading = null;
        if (updating === task) updating = null;
      });
    return task;
  };
  const refresh = (force = false) => {
    if (disposed || !read) return Promise.resolve(view.projection);
    if (updating) return updating.promise;
    if (reading && !force) return reading.promise;
    reading?.controller.abort();
    reading = perform('read', (signal) => read({ instanceId, signal }));
    return reading.promise;
  };
  const toggle = (item) => {
    if (!canToggle(disposed, write, item, targetType)) {
      return Promise.resolve(null);
    }
    const intent = { targetId: item.target_id, body: {
      enabled: item.enabled !== true, expected_revision: view.projection.revision,
    } };
    if (updating) {
      return sameIntent(updating.request, intent)
        ? updating.promise : Promise.reject(new Error(`${targetType}_update_pending`));
    }
    const request = sameIntent(failedRequest, intent) ? failedRequest : {
      ...intent, body: { ...intent.body, request_id: uuid() },
    };
    reading?.controller.abort();
    reading = null;
    updating = perform('write', (signal) => write(request.targetId, request.body, {
      instanceId, signal,
    }), request);
    return updating.promise;
  };
  return { instanceId, refresh, toggle,
    dispose: () => {
      disposed = true;
      generation += 1;
      reading?.controller.abort();
      updating?.controller.abort();
    },
  };
}
